import { useContext, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CreditCard } from "react-feather";
import { BrandContext } from "@/contexts/BrandContext/context";
import { paymentMethodsService } from "@/services/paymentMethods.service";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader } from "@/components/ui/dialog";
import { BrandPayoutMethodsEditor } from "./BrandPayoutMethodsEditor";

interface BrandPayoutMethodsPromptProps {
  /**
   * Why the brand is being asked now. The prompt only renders when the brand
   * has zero methods AND this is true — first affiliate, first sale, or a
   * pending payout.
   */
  when: boolean;
  title?: string;
  body?: string;
  /** Open the editor immediately instead of behind a button. */
  forceOpen?: boolean;
  onSaved?: () => void;
  className?: string;
}

/**
 * Brand-side moment-of-need prompt: "Choose how you'll pay affiliates."
 * Hidden until there is a reason to ask, gone once a method exists.
 */
export function BrandPayoutMethodsPrompt({
  when,
  title = "Choose how you'll pay affiliates",
  body = "Affiliates are earning. Pick at least one payout method so their requests can be paid.",
  forceOpen = false,
  onSaved,
  className = "",
}: BrandPayoutMethodsPromptProps) {
  const { brandDetails } = useContext(BrandContext);
  const [open, setOpen] = useState(false);

  const { data: supported, isLoading, refetch } = useQuery({
    queryKey: ["brandSupportedPaymentMethods", brandDetails?.id],
    queryFn: () =>
      brandDetails?.id
        ? paymentMethodsService.getBrandSupportedPaymentMethods(brandDetails.id)
        : Promise.resolve([]),
    enabled: !!brandDetails?.id,
  });

  if (!brandDetails?.id || isLoading || !when) return null;
  if ((supported?.length ?? 0) > 0) return null;

  const isOpen = open || forceOpen;

  return (
    <>
      <div
        className={`flex flex-col gap-3 rounded-xl border border-brand-600/20 bg-brand-600/5 px-4 py-3 sm:flex-row sm:items-center sm:justify-between ${className}`}
        role="status"
        data-testid="brand-payout-methods-prompt"
      >
        <div className="flex items-start gap-3">
          <CreditCard className="mt-0.5 shrink-0 text-brand-600" size={18} />
          <div>
            <p className="text-sm font-medium text-foreground">{title}</p>
            <p className="mt-0.5 text-xs text-muted-foreground">{body}</p>
          </div>
        </div>
        <Button size="sm" onClick={() => setOpen(true)} className="sm:shrink-0">
          Choose payout methods
        </Button>
      </div>

      <Dialog open={isOpen} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader showCloseButton onClose={() => setOpen(false)}>
            How will you pay affiliates?
          </DialogHeader>
          <BrandPayoutMethodsEditor
            brandDetailId={brandDetails.id}
            onCancel={() => setOpen(false)}
            onSaved={async () => {
              setOpen(false);
              await refetch();
              onSaved?.();
            }}
            saveLabel="Save payout methods"
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
